import React from 'react';
import { courseComparison } from '../mock';
import { Check, Crown } from 'lucide-react';
import { SectionHeader } from './Services';

const CourseComparison = () => {
  const { headers, rows, highlight } = courseComparison;
  // highlight refers to index within value array (0-based). EA is index 0.

  return (
    <section className="relative py-20 md:py-28 bg-paper-soft">
      <div className="squiggle-bg" />
      <div className="max-w-6xl mx-auto px-6 relative">
        <SectionHeader
          eyebrow="Compare Courses"
          title={<>EA vs <em className="italic">CPA • CMA • CA • CS</em></>}
          description="Why EA is India’s fastest, most affordable route to a global tax career. Side-by-side with every popular finance certification."
        />

        {/* Desktop table */}
        <div className="hidden md:block card-soft rounded-2xl overflow-hidden">
          <table className="w-full text-sm font-body">
            <thead>
              <tr>
                {headers.map((h, i) => {
                  const isEA = i === highlight + 1; // +1 because first header is 'Parameter'
                  return (
                    <th
                      key={i}
                      className={`p-4 md:p-5 text-left font-medium border-b border-[#143324]/10 ${
                        isEA
                          ? 'bg-[#143324] text-[#f5f1e6]'
                          : i === 0
                          ? 'bg-[#efe9d9] text-[#143324]'
                          : 'bg-[#efe9d9] text-[#143324]'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        {isEA && <Crown size={14} className="text-[#e9c46a]" fill="currentColor" />}
                        {h}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, ri) => (
                <tr key={ri} className={ri < rows.length - 1 ? 'border-b border-[#143324]/10' : ''}>
                  <td className="p-4 md:p-5 text-[#1c1c1c] font-medium">{row.label}</td>
                  {row.values.map((v, vi) => {
                    const isEA = vi === highlight;
                    return (
                      <td
                        key={vi}
                        className={`p-4 md:p-5 border-l border-[#143324]/10 text-[#1c1c1c] ${
                          isEA ? 'bg-[#f0ead9] text-[#143324] font-medium' : ''
                        }`}
                      >
                        {v}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile stacked cards */}
        <div className="md:hidden space-y-5">
          {headers.slice(1).map((course, ci) => {
            const isEA = ci === highlight;
            return (
              <div
                key={ci}
                className={`rounded-2xl overflow-hidden ${
                  isEA ? 'bg-[#143324] text-[#f5f1e6]' : 'card-soft'
                }`}
              >
                <div className="px-5 py-4 flex items-center justify-between">
                  <div className="font-body font-semibold text-lg flex items-center gap-2">
                    {isEA && <Crown size={16} className="text-[#e9c46a]" fill="currentColor" />}
                    {course}
                  </div>
                  {isEA && (
                    <span className="text-[10px] uppercase tracking-widest bg-[#f5f1e6]/20 px-2 py-1 rounded-full">Best pick</span>
                  )}
                </div>
                <div className={`divide-y ${isEA ? 'divide-[#f5f1e6]/10' : 'divide-[#143324]/10'}`}>
                  {rows.map((row, ri) => (
                    <div key={ri} className="px-5 py-3 flex items-center justify-between gap-4">
                      <span className={`text-[12.5px] font-body ${isEA ? 'text-[#c9c2a8]' : 'text-[#3d3d3d]'}`}>
                        {row.label}
                      </span>
                      <span className={`text-[13px] font-body text-right ${isEA ? 'text-[#f5f1e6] font-medium' : 'text-[#1c1c1c]'}`}>
                        {row.values[ci]}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Legend */}
        <div className="mt-8 flex items-center justify-center gap-3 text-[12px] text-[#3d3d3d] font-body">
          <Check size={13} className="text-[#143324]" strokeWidth={2.5} />
          <span>EA column highlighted — lowest cost, fastest duration, license to practise.</span>
        </div>
      </div>
    </section>
  );
};

export default CourseComparison;
